// Exotic contract surface — discriminated by `contract`. Vanilla / barrier
// variants carry an explicit `cp` (call/put) selector rather than baking
// call-vs-put into the contract name; OneTouch / DoubleNoTouch don't
// because they're direction-products, not call/put-products.

// Token identifies a pair in the live vol-surface registry via its
// `base_quote` string. Examples: 'BTC', 'ETH', 'SOL_USDC', 'TRUMP_USDT'.
// The set is no longer hard-coded — it comes from `list_vol_surface_tokens`
// at runtime via the useAvailableTokens hook.
export type Token = string;
export type PayType = 'at_touch' | 'at_expiry';
export type ModelKind = 'bs' | 'lv' | 'sv' | 'hybrid_lv_sv';
export type CallPut = 'call' | 'put';

export type ContractKind =
  | 'EuropeanOption'
  | 'AmericanOption'
  | 'OneTouchUpper'
  | 'OneTouchLower'
  | 'SingleBarrierUpper'
  | 'SingleBarrierLower'
  | 'DoubleNoTouch';

export const CONTRACT_LABELS: Record<ContractKind, string> = {
  EuropeanOption: 'European Option',
  AmericanOption: 'American Option',
  OneTouchUpper: 'OneTouch (Upper)',
  OneTouchLower: 'OneTouch (Lower)',
  SingleBarrierUpper: 'SingleBarrier (Upper)',
  SingleBarrierLower: 'SingleBarrier (Lower)',
  DoubleNoTouch: 'DoubleNoTouch',
};

export const PAY_TYPES: PayType[] = ['at_touch', 'at_expiry'];
export const MODELS: ModelKind[] = ['bs', 'lv', 'sv', 'hybrid_lv_sv'];
export const CALL_PUTS: CallPut[] = ['call', 'put'];

export interface ContractSchema {
  strike: boolean;
  upper: boolean;
  lower: boolean;
  payType: boolean;
  notionalChoosable: boolean;
  callPut: boolean;
}

export const CONTRACT_SCHEMA: Record<ContractKind, ContractSchema> = {
  EuropeanOption: {
    strike: true,
    upper: false,
    lower: false,
    payType: false,
    notionalChoosable: false,
    callPut: true,
  },
  AmericanOption: {
    strike: true,
    upper: false,
    lower: false,
    payType: false,
    notionalChoosable: false,
    callPut: true,
  },
  OneTouchUpper: {
    strike: false,
    upper: true,
    lower: false,
    payType: true,
    notionalChoosable: true,
    callPut: false,
  },
  OneTouchLower: {
    strike: false,
    upper: false,
    lower: true,
    payType: true,
    notionalChoosable: true,
    callPut: false,
  },
  SingleBarrierUpper: {
    strike: true,
    upper: true,
    lower: false,
    payType: false,
    notionalChoosable: false,
    callPut: true,
  },
  SingleBarrierLower: {
    strike: true,
    upper: false,
    lower: true,
    payType: false,
    notionalChoosable: false,
    callPut: true,
  },
  DoubleNoTouch: {
    strike: false,
    upper: true,
    lower: true,
    payType: false,
    notionalChoosable: true,
    callPut: false,
  },
};

// Common fields every instrument carries.
export interface BaseInstrument {
  id: string;
  token: Token;
  expiry: string;
  notional: number;
  model: ModelKind;
  mixing?: number; // only meaningful when model === 'hybrid_lv_sv'
}

// Per-contract discriminated branches.
export interface EuropeanOptionInstrument extends BaseInstrument {
  contract: 'EuropeanOption';
  strike: number;
  cp: CallPut;
  // notionalToken intentionally omitted — vanilla European options trade
  // in underlying units (like Barriers), so notional is implicitly base.
}

export interface AmericanOptionInstrument extends BaseInstrument {
  contract: 'AmericanOption';
  strike: number;
  cp: CallPut;
  // Same as European — notional fixed at the underlying.
}

export interface OneTouchUpperInstrument extends BaseInstrument {
  contract: 'OneTouchUpper';
  upper: number;
  payType: PayType;
  notionalToken: 'base' | 'quote';
}

export interface OneTouchLowerInstrument extends BaseInstrument {
  contract: 'OneTouchLower';
  lower: number;
  payType: PayType;
  notionalToken: 'base' | 'quote';
}

export interface SingleBarrierUpperInstrument extends BaseInstrument {
  contract: 'SingleBarrierUpper';
  strike: number;
  upper: number;
  cp: CallPut;
}

export interface SingleBarrierLowerInstrument extends BaseInstrument {
  contract: 'SingleBarrierLower';
  strike: number;
  lower: number;
  cp: CallPut;
}

export interface DoubleNoTouchInstrument extends BaseInstrument {
  contract: 'DoubleNoTouch';
  upper: number;
  lower: number;
  notionalToken: 'base' | 'quote';
}

export type Instrument =
  | EuropeanOptionInstrument
  | AmericanOptionInstrument
  | OneTouchUpperInstrument
  | OneTouchLowerInstrument
  | SingleBarrierUpperInstrument
  | SingleBarrierLowerInstrument
  | DoubleNoTouchInstrument;

// Merge a patch into an instrument and reconcile fields against the
// resulting contract schema:
//   - Fields the new kind does NOT declare are deleted.
//   - Fields the new kind DOES declare but are missing get a default.
//   - When the model leaves hybrid_lv_sv, mixing is dropped.
//   - Notional snaps to 100k (quote) / 1 (base) when the effective unit flips.
export function reconcileInstrument(prev: Instrument, patch: Partial<Instrument>): Instrument {
  const merged = { ...prev, ...patch } as Record<string, unknown>;

  const kind = merged.contract as ContractKind;
  const schema = CONTRACT_SCHEMA[kind];

  // Strip fields the new kind doesn't declare.
  if (!schema.strike) delete merged.strike;
  if (!schema.upper) delete merged.upper;
  if (!schema.lower) delete merged.lower;
  if (!schema.payType) delete merged.payType;
  if (!schema.notionalChoosable) delete merged.notionalToken;
  if (!schema.callPut) delete merged.cp;

  // Restore fields the new kind requires that may have been stripped by a
  // previous reconcile. Defaults are conservative — strike/upper/lower at 0
  // (user must set them before validateForCalculate passes), payType
  // at_expiry, notionalToken quote (USDC), cp 'call'.
  if (schema.strike && !('strike' in merged)) merged.strike = 0;
  if (schema.upper && !('upper' in merged)) merged.upper = 0;
  if (schema.lower && !('lower' in merged)) merged.lower = 0;
  if (schema.payType && !('payType' in merged)) merged.payType = 'at_expiry';
  if (schema.notionalChoosable && !('notionalToken' in merged)) {
    merged.notionalToken = 'quote';
  }
  if (schema.callPut && !('cp' in merged)) merged.cp = 'call';

  // Notional defaults follow the effective notional unit:
  //   USDC (quote)        -> 100,000
  //   Underlying (base /  -> 1
  //   barrier-implicit)
  // SingleBarrier has notionalChoosable=false, which strips notionalToken —
  // conceptually that means the underlying. Snap the value whenever the
  // effective unit flips so the user never sees stale 100k BTC or 1 USDC
  // notionals after a contract / unit change.
  const prevSchema = CONTRACT_SCHEMA[prev.contract];
  const prevPrev = prev as unknown as Record<string, unknown>;
  const effectiveUnit = (s: typeof schema, src: Record<string, unknown>): 'quote' | 'base' =>
    s.notionalChoosable ? ((src.notionalToken as 'quote' | 'base') ?? 'quote') : 'base';
  const prevUnit = effectiveUnit(prevSchema, prevPrev);
  const newUnit = effectiveUnit(schema, merged);
  if (prevUnit !== newUnit) {
    merged.notional = newUnit === 'quote' ? 100000 : 1;
  }

  if (merged.model !== 'hybrid_lv_sv') delete merged.mixing;

  return merged as unknown as Instrument;
}

// Client-side validation. Returns null when ready to send, or a
// human-readable message describing the first failure encountered.
export function validateForCalculate(i: Instrument): string | null {
  const schema = CONTRACT_SCHEMA[i.contract];

  if (!i.expiry) return 'Expiry is required';
  if (new Date(i.expiry).getTime() <= Date.now()) return 'Expiry must be in the future';
  if (!Number.isFinite(i.notional) || i.notional === 0) {
    return 'Notional must be non-zero';
  }

  if (schema.strike && !('strike' in i)) return 'Strike is required';
  if (schema.upper && !('upper' in i)) return 'Upper barrier is required';
  if (schema.lower && !('lower' in i)) return 'Lower barrier is required';
  if (schema.payType && !('payType' in i)) return 'Pay type is required';
  if (schema.notionalChoosable && !('notionalToken' in i)) return 'Notional token is required';
  if (schema.callPut && !('cp' in i)) return 'Call/Put is required';

  if (i.contract === 'DoubleNoTouch' && i.upper <= i.lower) {
    return 'Upper barrier must exceed lower';
  }

  if (i.model === 'hybrid_lv_sv') {
    if (i.mixing === undefined) return 'Mixing weight is required for hybrid model';
    if (i.mixing < 0 || i.mixing > 1) return 'Mixing weight must be in [0, 1]';
  }

  return null;
}
